import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Camera,
  CheckCircle,
  Loader2,
  Minus,
  Package,
  Plus,
  Search,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import BaseApi from "../../services/base-api";
import { ReturnsApi } from "../../services/returns-api";
import { useToast } from "../../context/ToastContext";
import Price from "../Currency/Price";
import { ImageWithFallback } from "../../constants/image-placeholder";
import { Order, OrderItems } from "../../types/Order";
import { Product, ProductVariation } from "../../types/Product";
import { ReturnRequestType } from "../../types/ReturnRequest";

interface ReturnRequestModalProps {
  order: Order;
  type: ReturnRequestType;
  returnPolicyDays: number;
  onClose: () => void;
  onCreated: (requestId: string) => void;
  // Staff filing on behalf of the customer (admin Orders page)
  isStaff?: boolean;
}

type SelectedItemState = Record<
  string,
  {
    selected: boolean;
    quantity: number;
    reason: string;
  }
>;

const REASONS = [
  "Wrong size",
  "Damaged item",
  "Item not as described",
  "Changed my mind",
  "Wrong item received",
  "Other",
];

const getLocalizedText = (value: any, language: string, fallback = "") => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  return (
    (language === "ar" ? value.ar || value.en : value.en || value.ar) ||
    fallback
  );
};

const formatVariation = (variation?: ProductVariation | null) => {
  const entries = Object.entries(variation?.attributes || {});
  return entries.map(([key, value]) => `${key}: ${value}`).join(" - ");
};

const ReturnRequestModal: React.FC<ReturnRequestModalProps> = ({
  order,
  type,
  returnPolicyDays,
  onClose,
  onCreated,
  isStaff = false,
}) => {
  const { t, i18n } = useTranslation();
  const { success, err } = useToast();
  const [selectedItems, setSelectedItems] = useState<SelectedItemState>({});
  const [exchangeItemId, setExchangeItemId] = useState("");
  const [reason, setReason] = useState(REASONS[0]);
  const [comments, setComments] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [replacementProduct, setReplacementProduct] = useState<Product | null>(
    null,
  );
  const [replacementVariationId, setReplacementVariationId] = useState("");

  const selectedOrderItem = useMemo(
    () => order.items.find((item) => item.id === exchangeItemId),
    [exchangeItemId, order.items],
  );

  useEffect(() => {
    const initial = order.items.reduce<SelectedItemState>((acc, item) => {
      acc[item.id] = {
        selected: type === "return",
        quantity: item.quantity || 1,
        reason: "",
      };
      return acc;
    }, {});
    setSelectedItems(initial);
    setExchangeItemId(order.items[0]?.id || "");
  }, [order.items, type]);

  useEffect(() => {
    if (type !== "exchange") return;

    const timer = setTimeout(async () => {
      try {
        setProductsLoading(true);
        const params = new URLSearchParams({
          page: "1",
          limit: "12",
          isActive: "true",
        });
        if (productSearch.trim()) {
          params.append("search", productSearch.trim());
        }
        const res = await BaseApi.get(`/products?${params.toString()}`);
        setProducts(Array.isArray(res?.data) ? res.data : []);
      } catch (error) {
        console.error("Failed to fetch products", error);
      } finally {
        setProductsLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [productSearch, type]);

  const toggleReturnItem = (itemId: string) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        selected: !prev[itemId]?.selected,
      },
    }));
  };

  const updateReturnQuantity = (item: OrderItems, quantity: number) => {
    setSelectedItems((prev) => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        quantity: Math.min(Math.max(1, quantity), item.quantity),
      },
    }));
  };

  const updateReturnReason = (itemId: string, value: string) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        reason: value,
      },
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files || []);
    setFiles((prev) => [...prev, ...nextFiles].slice(0, 5));
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const selectReplacementProduct = async (product: Product) => {
    setReplacementVariationId("");
    try {
      const res = await BaseApi.get(`/products/${product.id}?isActive=true`);
      setReplacementProduct(res?.data || product);
    } catch (error) {
      setReplacementProduct(product);
    }
  };

  const buildItemsPayload = () => {
    if (type === "exchange") {
      const item = selectedOrderItem;
      if (!item) return [];
      return [
        {
          orderItemId: item.id,
          quantity: 1,
          reason,
        },
      ];
    }

    return order.items
      .filter((item) => selectedItems[item.id]?.selected)
      .map((item) => ({
        orderItemId: item.id,
        quantity: selectedItems[item.id]?.quantity || item.quantity,
        reason: selectedItems[item.id]?.reason || reason,
      }));
  };

  const validate = () => {
    const items = buildItemsPayload();
    if (!items.length) {
      err(t("Select at least one item"));
      return false;
    }

    if (type === "exchange") {
      if (items.length !== 1) {
        err(t("Select exactly one item to exchange"));
        return false;
      }
      if (!replacementProduct?.id) {
        err(t("Select a replacement product"));
        return false;
      }
      if (
        replacementProduct.hasVariations &&
        (replacementProduct.variations || []).length > 0 &&
        !replacementVariationId
      ) {
        err(t("Select a replacement variation"));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);
      const mediaUrls: string[] = [];

      for (const file of files) {
        const url = await ReturnsApi.uploadMedia(file);
        mediaUrls.push(url);
      }

      const res = await ReturnsApi.create({
        orderId: order.id,
        type,
        items: buildItemsPayload(),
        reason,
        comments: comments.trim() || undefined,
        mediaUrls,
        replacementProductId:
          type === "exchange" ? replacementProduct?.id : undefined,
        replacementVariationId:
          type === "exchange" ? replacementVariationId || undefined : undefined,
      });

      if (res?.success && res?.data?.id) {
        success(t("Request submitted successfully"));
        onCreated(res.data.id);
      } else {
        err(res?.message || t("Failed to submit request"));
      }
    } catch (error: any) {
      console.error("Failed to submit return request", error);
      err(error?.message || t("Failed to submit request"));
    } finally {
      setSubmitting(false);
    }
  };

  const modalTitle = isStaff
    ? type === "exchange"
      ? t("Create Exchange")
      : t("Create Return")
    : type === "exchange"
      ? t("Request Exchange")
      : t("Request Return");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-black px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold text-white">{modalTitle}</h2>
            <p className="mt-1 text-sm text-gray-200">
              {t("Order")} #{order.orderNumber} -{" "}
              {t("Returnable/exchangeable within")} {returnPolicyDays}{" "}
              {t("days of delivery")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-2 text-white transition-colors hover:bg-white/10 disabled:opacity-60"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="max-h-[calc(92vh-88px)] overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-700" />
                <p className="text-sm text-blue-900">
                  {type === "exchange"
                    ? t(
                        "Exchange requests are one-for-one. Select one delivered item and choose its replacement.",
                      )
                    : t(
                        "Select the delivered items you want to return. Quantities cannot exceed the ordered quantity.",
                      )}
                </p>
              </div>
            </div>

            <section>
              <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-gray-900">
                <Package className="h-5 w-5" />
                {type === "exchange"
                  ? t("Item to exchange")
                  : t("Items to return")}
              </h3>
              <div className="space-y-3">
                {order.items.map((item) => {
                  const itemState = selectedItems[item.id];
                  const checked =
                    type === "exchange"
                      ? exchangeItemId === item.id
                      : itemState?.selected;

                  return (
                    <div
                      key={item.id}
                      className={`rounded-xl border-2 p-4 transition-colors ${
                        checked
                          ? "border-black bg-gray-50"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                        <label className="flex flex-1 cursor-pointer items-start gap-4">
                          <input
                            type={type === "exchange" ? "radio" : "checkbox"}
                            checked={!!checked}
                            onChange={() =>
                              type === "exchange"
                                ? setExchangeItemId(item.id)
                                : toggleReturnItem(item.id)
                            }
                            className="mt-8 h-5 w-5 accent-black"
                          />
                          <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                            <ImageWithFallback
                              src={item.product?.images?.[0]}
                              alt={getLocalizedText(item.productName, i18n.language)}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-semibold text-gray-900">
                              {getLocalizedText(item.productName, i18n.language)}
                            </h4>
                            {item.variationDetails && (
                              <p className="mt-1 text-sm text-gray-500">
                                {typeof item.variationDetails === "string"
                                  ? item.variationDetails
                                  : formatVariation(
                                      item.variationDetails as any,
                                    ) || (item.variationDetails as any)?.sku}
                              </p>
                            )}
                            <p className="mt-1 text-sm text-gray-600">
                              {t("Ordered Quantity")}: {item.quantity}
                            </p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              <Price amount={item.total} />
                            </p>
                          </div>
                        </label>

                        {type === "return" && itemState?.selected && (
                          <div className="flex flex-col gap-3 sm:w-56">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  updateReturnQuantity(
                                    item,
                                    itemState.quantity - 1,
                                  )
                                }
                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-200 hover:bg-gray-300"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <input
                                type="number"
                                min="1"
                                max={item.quantity}
                                value={itemState.quantity}
                                onChange={(event) =>
                                  updateReturnQuantity(
                                    item,
                                    Number(event.target.value) || 1,
                                  )
                                }
                                className="h-9 w-16 rounded-lg border-2 border-gray-200 text-center font-semibold focus:border-black focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  updateReturnQuantity(
                                    item,
                                    itemState.quantity + 1,
                                  )
                                }
                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-200 hover:bg-gray-300"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                            <input
                              type="text"
                              value={itemState.reason}
                              onChange={(event) =>
                                updateReturnReason(item.id, event.target.value)
                              }
                              placeholder={t("Item reason optional")}
                              className="rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-black focus:outline-none"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {type === "exchange" && (
              <section className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {t("Replacement product")}
                </h3>
                <div className="relative">
                  <Search className="absolute start-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(event) => setProductSearch(event.target.value)}
                    placeholder={t("Search by product name or SKU")}
                    className="w-full rounded-xl border-2 border-gray-200 py-3 pe-4 ps-10 focus:border-black focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {productsLoading ? (
                    <div className="col-span-full flex items-center justify-center py-8 text-gray-500">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t("Loading...")}
                    </div>
                  ) : (
                    products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => selectReplacementProduct(product)}
                        className={`flex items-center gap-3 rounded-xl border-2 p-3 text-start transition-colors ${
                          replacementProduct?.id === product.id
                            ? "border-black bg-gray-50"
                            : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                          <ImageWithFallback
                            src={product.images?.[0]}
                            alt={getLocalizedText(product.name, i18n.language)}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-gray-900">
                            {getLocalizedText(product.name, i18n.language)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t("SKU")}: {product.sku}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            <Price amount={product.price} />
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {replacementProduct?.hasVariations &&
                  (replacementProduct.variations || []).length > 0 && (
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-700">
                        {t("Replacement variation")}
                      </label>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {replacementProduct.variations.map((variation) => (
                          <button
                            key={variation.id}
                            type="button"
                            disabled={variation.stock <= 0}
                            onClick={() =>
                              setReplacementVariationId(variation.id)
                            }
                            className={`rounded-lg border-2 p-3 text-start text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                              replacementVariationId === variation.id
                                ? "border-black bg-gray-50"
                                : "border-gray-200 hover:border-gray-400"
                            }`}
                          >
                            <span className="font-semibold text-gray-900">
                              {formatVariation(variation) ||
                                variation.sku ||
                                t("Variation")}
                            </span>
                            <span className="mt-1 block text-gray-500">
                              {t("Stock")}: {variation.stock}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
              </section>
            )}

            <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  {t("Reason")}
                </label>
                <select
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-black focus:outline-none"
                >
                  {REASONS.map((item) => (
                    <option key={item} value={item}>
                      {t(item)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  {t("Attachments")}
                </label>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 px-4 py-3 text-sm font-semibold text-gray-600 hover:border-black hover:text-black">
                  <Camera className="h-5 w-5" />
                  {t("Add photos or videos")}
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>
            </section>

            {files.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700"
                  >
                    <span className="max-w-[180px] truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                {t("Comments")}
              </label>
              <textarea
                value={comments}
                onChange={(event) => setComments(event.target.value)}
                rows={4}
                placeholder={t("Add any details that help us review this")}
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-black focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-col-reverse gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-xl border border-gray-300 px-5 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <CheckCircle className="h-5 w-5" />
            )}
            {submitting ? t("Submitting...") : t("Submit Request")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReturnRequestModal;
